import React, { useState, useRef, useEffect } from "react";
import { User, LogOut, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { authService } from "../api/authService";
import { userService } from "../api/userService";
import StoreSelector from "./StoreSelector";
import { useSidebarConfig } from "../contexts/SidebarConfigContext";

function readProfileIdentity() {
  const userData = authService.getCurrentUser() || {};
  const email = userData?.email || localStorage.getItem("email") || "";
  const username =
    userData?.full_name ||
    userData?.name ||
    localStorage.getItem("username") ||
    email ||
    "User";
  const avatarUrl =
    localStorage.getItem("avatar_url") || userData?.avatar_url || null;
  return { username, email, avatarUrl };
}

export default function Header({ title, subtitle } = {}) {
  const navigate = useNavigate();
  const { config, toggleMobile } = useSidebarConfig();
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [profileIdentity, setProfileIdentity] = useState(() =>
    readProfileIdentity(),
  );
  const menuRef = useRef(null);

  // Keep profile data synced without waiting for user interactions.
  useEffect(() => {
    const syncProfile = () => {
      setProfileIdentity(readProfileIdentity());
    };

    syncProfile();

    const handleAvatarUpdate = (e) => {
      if (e?.detail?.avatarUrl) {
        localStorage.setItem("avatar_url", e.detail.avatarUrl);
      }
      syncProfile();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        syncProfile();
      }
    };

    window.addEventListener("avatarUpdated", handleAvatarUpdate);
    window.addEventListener("storage", syncProfile);
    window.addEventListener("focus", syncProfile);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Fetch profile data on mount to ensure realtime availability of avatar
    let cancelled = false;
    userService.getProfile()
      .then((response) => {
        if (cancelled) return;
        const data = response.data;
        if (data.avatar_url) {
          localStorage.setItem("avatar_url", data.avatar_url);
        } else {
          localStorage.removeItem("avatar_url");
        }
        if (data.full_name) {
          localStorage.setItem("username", data.full_name);
        }
        syncProfile();
      })
      .catch(() => {});

    return () => {
      cancelled = true;
      window.removeEventListener("avatarUpdated", handleAvatarUpdate);
      window.removeEventListener("storage", syncProfile);
      window.removeEventListener("focus", syncProfile);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await authService.logout();
      navigate("/login", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/login", { replace: true });
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowProfileMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close dropdown when pressing Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setShowProfileMenu(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleProfileClick = () => {
    setShowProfileMenu(false);
    navigate("/profile");
  };

  return (
    <div className="bg-white/85 backdrop-blur-md border-b border-slate-200/70 px-6 lg:px-8 flex items-center justify-between sticky top-0 z-30 h-16">
      {/* Left: hamburger (mobile) + store selector */}
      <div className="flex-1 min-w-0 flex items-center gap-3">
        {config.mobileHide && (
          <button
            onClick={toggleMobile}
            className="md:hidden p-2 -ml-2 rounded-md text-slate-600 hover:bg-slate-100 transition-colors"
            title="Menu"
            aria-label="Buka menu">
            <Menu className="w-5 h-5" />
          </button>
        )}
        <StoreSelector
          onSwitch={() => {
            // Reload current page so the new tenant's data is fetched cleanly.
            if (typeof window !== "undefined") {
              window.location.reload();
            }
          }}
        />
        {title && (
          <div className="hidden lg:block min-w-0 pl-4 ml-2 border-l border-slate-200">
            <h1 className="text-sm font-semibold text-slate-700 truncate leading-tight">
              {title}
            </h1>
            {subtitle && (
              <p className="text-xs text-slate-500 truncate leading-tight">
                {subtitle}
              </p>
            )}
          </div>
        )}
      </div>
      <div className="flex items-center gap-2">
        {/* Profile Dropdown */}
        <div className="relative" ref={menuRef}>
          <button
            onClick={() => setShowProfileMenu((prev) => !prev)}
            className="flex items-center gap-2 p-1 pr-2 rounded-full hover:bg-slate-100 transition-colors">
            <div className="w-9 h-9 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center overflow-hidden ring-2 ring-white shadow-sm">
              {profileIdentity.avatarUrl ? (
                <img
                  src={profileIdentity.avatarUrl}
                  alt="Avatar"
                  className="w-full h-full object-cover"
                />
              ) : (
                <User className="w-4 h-4 text-white" />
              )}
            </div>
          </button>

          {showProfileMenu && (
            <div className="absolute right-0 mt-2 w-60 bg-white rounded-xl shadow-lg ring-1 ring-slate-200/70 py-1 z-50">
              {/* User info */}
              <div className="px-4 py-3 border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-700 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {profileIdentity.avatarUrl ? (
                      <img
                        src={profileIdentity.avatarUrl}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <User className="w-5 h-5 text-white" />
                    )}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {profileIdentity.username}
                    </p>
                    {profileIdentity.email && (
                      <p className="text-xs text-slate-500 truncate">
                        {profileIdentity.email}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Menu items */}
              <div className="py-1">
                <button
                  onClick={handleProfileClick}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
                  <User className="w-4 h-4 text-slate-400" />
                  Profile
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-rose-600 hover:bg-rose-50 transition-colors">
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
